// Shared powerup definitions + icon drawers used by PongGame (Bong host)
// and Controller (mirror). Keep host & controller in lockstep on visuals.
//
// Per user iter (Bong retheme): the buff/debuff system is REDUCED to just
// two ball-speed power-ups themed to the cannabis aesthetic:
//   • slow  → SYRINGE icon — makes the ball very slow
//   • fast  → TINY BAG OF WHITE POWDER icon — makes the ball fast

export const POWERUP_DEFS = {
  slow: { name: "Syringe",   icon: "syringe", fill: "#86EFAC", glow: "#86EFAC" }, // pale green serum
  fast: { name: "Powder",    icon: "powder",  fill: "#F5F1E8", glow: "#FFFFFF" }, // bone white
  // Legacy aliases kept so any in-flight pickups don't crash if a state
  // object still references them.  They behave like their replacements.
  speed:  { name: "Powder",  icon: "powder",  fill: "#F5F1E8", glow: "#FFFFFF" },
  grow:   { name: "Powder",  icon: "powder",  fill: "#F5F1E8", glow: "#FFFFFF" },
  shrink: { name: "Syringe", icon: "syringe", fill: "#86EFAC", glow: "#86EFAC" },
  hydra:  { name: "Powder",  icon: "powder",  fill: "#F5F1E8", glow: "#FFFFFF" },
  aegis:  { name: "Syringe", icon: "syringe", fill: "#86EFAC", glow: "#86EFAC" },
};

// Draw a small icon centered at (x, y). Designed for ~36px diameter discs.
export const drawPowerupIcon = (ctx, type, x, y) => {
  const def = POWERUP_DEFS[type];
  if (!def) return;
  ctx.save();
  ctx.strokeStyle = "#1A0A2E";
  ctx.fillStyle = "#1A0A2E";
  ctx.lineWidth = 1.8;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  switch (def.icon) {
    case "syringe": drawSyringe(ctx, x, y); break;
    case "powder":  drawPowderBag(ctx, x, y); break;
    default: break;
  }
  ctx.restore();
};

// --- Individual icon paths ---------------------------------------------------

function drawSyringe(ctx, x, y) {
  // Diagonal syringe (top-left → bottom-right): needle tip → needle hub →
  // barrel with plunger → thumb-rest. Drawn rotated 30° for legibility.
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-Math.PI / 6);
  // Needle (thin line from far-left to barrel)
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(-15, 0);
  ctx.lineTo(-6, 0);
  ctx.stroke();
  // Hub (small bevel between needle and barrel)
  ctx.fillRect(-6, -1.5, 2.5, 3);
  // Barrel
  ctx.lineWidth = 1.6;
  ctx.strokeRect(-3.5, -3.5, 12, 7);
  // Liquid level / fill stripe inside barrel (left half tinted)
  ctx.fillStyle = "rgba(26,10,46,0.55)";
  ctx.fillRect(-2, -2.5, 6, 5);
  ctx.fillStyle = "#1A0A2E";
  // Plunger rod (extending right out of barrel)
  ctx.beginPath();
  ctx.moveTo(8.5, 0);
  ctx.lineTo(13, 0);
  ctx.stroke();
  // Thumb-rest (flat cap at far right)
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(13, -3);
  ctx.lineTo(13, 3);
  ctx.stroke();
  // Barrel graduation ticks
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(0, -3.5); ctx.lineTo(0, -2.2);
  ctx.moveTo(3, -3.5); ctx.lineTo(3, -2.2);
  ctx.moveTo(6, -3.5); ctx.lineTo(6, -2.2);
  ctx.stroke();
  ctx.restore();
}

function drawPowderBag(ctx, x, y) {
  // Tiny twist-tie baggie of white powder: trapezoid body bulging at base,
  // gathered/tied at the top.  Stippled dots inside hint at powder grain.
  ctx.save();
  ctx.translate(x, y);
  // Tied top (V-shape at the top of the bag)
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-4, -10);
  ctx.lineTo(0, -7);
  ctx.lineTo(4, -10);
  ctx.stroke();
  // Bag body — wider at the bottom, narrow at the tied neck.
  ctx.beginPath();
  ctx.moveTo(-3, -7);
  ctx.lineTo(-8, 9);
  ctx.quadraticCurveTo(0, 12, 8, 9);
  ctx.lineTo(3, -7);
  ctx.closePath();
  ctx.stroke();
  // Powder fill — semi-transparent infill
  ctx.fillStyle = "rgba(26,10,46,0.10)";
  ctx.beginPath();
  ctx.moveTo(-3, -6);
  ctx.lineTo(-7.5, 8.5);
  ctx.quadraticCurveTo(0, 11.5, 7.5, 8.5);
  ctx.lineTo(3, -6);
  ctx.closePath();
  ctx.fill();
  // Powder grain stipple
  ctx.fillStyle = "#1A0A2E";
  const dots = [
    [-3, 0], [2, -1], [-1, 4], [4, 5], [-4, 6], [1, 2], [-2, -3], [3, 2], [0, 7], [5, 0],
  ];
  for (const [dx, dy] of dots) {
    ctx.beginPath();
    ctx.arc(dx, dy, 0.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// Draw a glass bong pipe icon at (x, y).  Used by the periodic "bong pipe"
// event that appears on player 1's or player 2's side of the table while
// the boiling/gurgling sound plays.  ~48px tall.
export const drawBongPipe = (ctx, x, y, scale = 1) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  // Outer glass strokes
  ctx.strokeStyle = "rgba(192,132,252,0.95)";
  ctx.fillStyle = "rgba(192,132,252,0.18)";
  ctx.lineWidth = 1.8;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Spherical base / chamber
  ctx.beginPath();
  ctx.arc(0, 12, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Long neck rising from the chamber
  ctx.beginPath();
  ctx.moveTo(-4.5, 4);
  ctx.lineTo(-3.5, -18);
  ctx.lineTo(3.5, -18);
  ctx.lineTo(4.5, 4);
  ctx.stroke();
  // Flared mouthpiece lip at top
  ctx.beginPath();
  ctx.moveTo(-6.5, -20);
  ctx.lineTo(-3.5, -18);
  ctx.moveTo(6.5, -20);
  ctx.lineTo(3.5, -18);
  ctx.stroke();
  // Down-stem (diagonal tube into the chamber)
  ctx.beginPath();
  ctx.moveTo(4, 4);
  ctx.lineTo(11, 16);
  ctx.lineTo(13, 14);
  ctx.lineTo(6, 2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Bowl on the end of the down-stem
  ctx.beginPath();
  ctx.arc(14, 13, 3.4, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,140,90,0.65)";
  ctx.fill();
  ctx.strokeStyle = "rgba(192,132,252,0.95)";
  ctx.stroke();
  // Water line inside chamber — wavy
  ctx.strokeStyle = "rgba(192,132,252,0.7)";
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(-10, 13);
  ctx.bezierCurveTo(-6, 11, -2, 15, 0, 13);
  ctx.bezierCurveTo(2, 11, 6, 15, 10, 13);
  ctx.stroke();
  // Bubbles rising from the down-stem
  ctx.fillStyle = "rgba(240,230,255,0.85)";
  ctx.beginPath();
  ctx.arc(3, 8, 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-2, 4, 1.0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(1, -2, 0.9, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};
